import { whenLabel } from "../format";
import { groupLabel, type SessionContact } from "../protocol";
import type { GroupEntry } from "../hooks/useGroups";
import type { GroupSummary } from "../hooks/useGroupUnreads";
import { toggleFamily, useExpandedFamilies } from "../hooks/useExpandedFamilies";
import { distinctBranches, foldQuietChildren, middleEllipsis, sessionRows } from "../sessionTree";

/** Longest branch chip a sidebar row draws before cutting it from the middle. */
const BRANCH_CHIP_MAX = 18;

/** The session-contacts list + search filter. Presentational — data + the selection setter come as props. */
export function Sidebar({
  sessionsReady,
  sessions,
  orderedSessions,
  sessionQuery,
  setSessionQuery,
  previews,
  unreads,
  selected,
  setSelected,
  groups,
  groupUnreads,
  selectedGroup,
  onSelectGroup,
  onNewGroup,
}: {
  sessionsReady: boolean;
  sessions: SessionContact[];
  orderedSessions: SessionContact[];
  sessionQuery: string;
  setSessionQuery: (q: string) => void;
  previews: Record<string, { text: string; ts: number }>;
  /** Unread messages per session, for the badge. Saturates at the listener cap. */
  unreads?: Record<string, number>;
  selected: string | null;
  setSelected: (key: string) => void;
  /** #Group-chat channels across pairings — a pairing-level section above the per-session list. */
  groups?: GroupEntry[];
  /** Per-group summary (unread badge + last-message preview) keyed by "pairingId/groupId". */
  groupUnreads?: Record<string, GroupSummary>;
  selectedGroup?: string | null;
  onSelectGroup?: (key: string) => void;
  onNewGroup?: () => void;
}) {
  const expanded = useExpandedFamilies();
  const query = sessionQuery.trim().toLowerCase();
  const branches = distinctBranches(orderedSessions);
  // Nesting is applied AFTER the search filter, so a child whose parent the query excluded shows up at the top
  // level instead of vanishing with it. A search unfolds everything, or a match could hide behind a fold.
  const rows = sessionRows(
    orderedSessions.filter((s) => {
      if (!query) return true;
      const k = `${s.pairingId}/${s.sessionId}`;
      return (
        s.label.toLowerCase().includes(query) ||
        s.branch.toLowerCase().includes(query) ||
        (previews[k]?.text ?? "").toLowerCase().includes(query)
      );
    }),
  );
  const entries = query
    ? rows.map((row) => ({ kind: "session" as const, row }))
    : foldQuietChildren(rows, expanded, (s) => {
        const k = `${s.pairingId}/${s.sessionId}`;
        return k === selected || (unreads?.[k] ?? 0) > 0;
      });
  return (
    <aside className="sidebar">
      {onNewGroup && (
        <div className="sidebar-groups">
          <div className="sidebar-groups-head">
            <span>Groups</span>
            <button className="new-group-btn" onClick={onNewGroup} title="New group">
              +
            </button>
          </div>
          {(groups ?? []).map((g) => {
            const key = `${g.pairingId}/${g.group.id}`;
            return (
              <button
                key={key}
                className={`row group-row${selectedGroup === key ? " active" : ""}`}
                onClick={() => onSelectGroup?.(key)}
              >
                <span className="group-icon">👥</span>
                <div className="row-main">
                  <div className="row-top">
                    <span className="label">{groupLabel(g.group)}</span>
                    {(groupUnreads?.[key]?.unread ?? 0) > 0 ? <span className="unread-badge">{groupUnreads![key].unread}</span> : null}
                  </div>
                  <div className="row-preview">
                    {groupUnreads?.[key]?.preview || `${g.group.participants.length} participants`}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
      {sessionsReady && sessions.length > 0 && (
        <div className="sidebar-search">
          <input
            value={sessionQuery}
            onChange={(e) => setSessionQuery(e.target.value)}
            placeholder="Search sessions…"
          />
        </div>
      )}
      {!sessionsReady ? (
        <div className="skeleton-list">
          {[0, 1, 2].map((i) => (
            <div key={i} className="skeleton-row" />
          ))}
        </div>
      ) : sessions.length === 0 ? (
        <p className="muted" style={{ padding: 12 }}>
          Paired — waiting for a session. Run <code>/failover:on</code> in a Claude Code session.
        </p>
      ) : (
        entries.map((e) => {
          if (e.kind === "fold") {
            return (
              <button
                key={`fold:${e.parentKey}`}
                className="session-fold"
                aria-expanded={e.expanded}
                onClick={() => toggleFamily(e.parentKey)}
              >
                {e.expanded ? "▾ Show fewer" : `▸ ${e.hidden} more worktree${e.hidden === 1 ? "" : "s"}`}
              </button>
            );
          }
          const { session: s, depth } = e.row;
          const key = `${s.pairingId}/${s.sessionId}`;
          // Server up but no agent attached — outranks the activity line and the awaiting text.
          const away = s.online && s.agentAttached === false;
          const branch = branches.get(key);
          return (
            <button
              key={key}
              className={`row${selected === key ? " active" : ""}${depth ? " child" : ""}`}
              onClick={() => setSelected(key)}
            >
              <span
                className={`dot${
                  s.online ? (away ? " away" : s.awaiting ? " awaiting" : s.idle ? " idle" : " on") : s.asleep ? " asleep" : ""
                }`}
                title={s.asleep ? "Asleep: a message wakes it" : !s.online && s.host === "conductor" ? "In Conductor: messages wait until you open it there" : undefined}
              />
              <div className="row-main">
                <div className="row-top">
                  <span className="label">{s.label}</span>
                  {/* Shown by what the session IS, not by its indent: a child whose parent is missing sits at
                      the top level, and that is exactly where nothing else would distinguish it. */}
                  {branch ? (
                    <span className="row-branch" title={s.branch}>
                      {middleEllipsis(branch, BRANCH_CHIP_MAX)}
                    </span>
                  ) : null}
                  {previews[key]?.ts ? <span className="row-time">{whenLabel(previews[key].ts)}</span> : null}
                  {(unreads?.[key] ?? 0) > 0 ? <span className="unread-badge">{unreads![key]}</span> : null}
                </div>
                {away ? (
                  <div className="row-preview">Away</div>
                ) : s.online && s.activityLine ? (
                  <div className="row-preview activity">{s.activityLine}</div>
                ) : s.online && s.awaiting ? (
                  <div className="row-preview">Awaiting your reply</div>
                ) : previews[key]?.text ? (
                  <div className="row-preview">{previews[key].text}</div>
                ) : null}
              </div>
            </button>
          );
        })
      )}
    </aside>
  );
}
